import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DistributionDonutChart, ServiceDailyComposedChart } from "../../admin-shared/analytics/AnalyticsChartKit";
import Loader from "../components/Loader";
import { fetchPartnerServiceAnalytics } from "../utils/partnerServiceAnalyticsApi";
import HqFranchiseFilterField from "../../admin-shared/analytics/HqFranchiseFilterField";
import "./PartnerCheckinAnalytics.css";

const todayIso = () => {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
};

const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");
const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const MetricCard = ({ label, value, hint }) => (
  <article className="pca-metric">
    <p className="pca-metric__label">{label}</p>
    <p className="pca-metric__value">{value}</p>
    {hint ? <p className="pca-metric__hint">{hint}</p> : null}
  </article>
);

export default function PartnerServiceAnalytics({
  portalBase = "/naturals/partner",
  portalLabel = "Franchise partner",
  title = "Service analytics",
  subtitle = "Price-list catalog vs services taken on visits (completed, pending, analysis-sourced).",
  fetchServiceAnalytics = fetchPartnerServiceAnalytics,
  hqApi = null,
}) {
  const [franchiseId, setFranchiseId] = useState("");
  const [fromDate, setFromDate] = useState(() => daysAgoIso(29));
  const [toDate, setToDate] = useState(() => todayIso());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchServiceAnalytics({
        fromDate,
        toDate,
        franchiseId: franchiseId || undefined,
      });
      setAnalytics(data);
    } catch (err) {
      setError(err?.message || "Failed to load service analytics");
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, franchiseId, fetchServiceAnalytics]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = analytics?.summary || {};
  const taken = useMemo(
    () => (Array.isArray(analytics?.takenServices) ? analytics.takenServices : []),
    [analytics],
  );
  const untaken = useMemo(
    () => (Array.isArray(analytics?.untakenServices) ? analytics.untakenServices : []),
    [analytics],
  );
  const daily = useMemo(
    () => (Array.isArray(analytics?.daily) ? analytics.daily : []),
    [analytics],
  );
  const catalogByType = useMemo(
    () => (Array.isArray(analytics?.catalogByType) ? analytics.catalogByType : []),
    [analytics],
  );

  if (loading && !analytics) return <Loader />;

  return (
    <div className="pca-page">
      <div className="pca-page__header">
        <div>
          <p className="pca-page__eyebrow">{portalLabel}</p>
          <h1 className="pca-page__title">{title}</h1>
          <p className="pca-page__subtitle">{subtitle}</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {portalBase.includes("/partner") ? (
            <>
              <Link to={`${portalBase}/price-list`} className="pca-btn pca-btn--ghost">
                Price list
              </Link>
              <Link to={`${portalBase}/visit-services`} className="pca-btn pca-btn--ghost">
                Visit services
              </Link>
            </>
          ) : null}
          <Link to={`${portalBase}/overview`} className="pca-btn pca-btn--ghost">
            ← Overview
          </Link>
        </div>
      </div>

      <div className="pca-filters">
        {hqApi ? (
          <HqFranchiseFilterField api={hqApi} value={franchiseId} onChange={setFranchiseId} />
        ) : null}
        <label>
          From
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className="pca-btn" onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Apply"}
        </button>
      </div>

      {error ? <p className="pca-error">{error}</p> : null}

      <div className="pca-metrics">
        <MetricCard
          label="Catalog active"
          value={formatNumber(summary.catalogActive)}
          hint={`${formatNumber(summary.catalogTotal)} total on price list`}
        />
        <MetricCard
          label="Taken lines"
          value={formatNumber(summary.takenLines)}
          hint={`${formatNumber(summary.visitsWithServices)} visits with services`}
        />
        <MetricCard
          label="Completed lines"
          value={formatNumber(summary.completedLines)}
          hint={`${formatNumber(summary.pendingLines)} pending · ${formatNumber(summary.inProgressLines)} in progress`}
        />
        <MetricCard
          label="From analysis"
          value={formatNumber(summary.fromAnalysis)}
          hint={`${formatNumber(summary.fromDesk)} desk / welcome selected`}
        />
        <MetricCard
          label="Taken sales"
          value={`₹ ${formatMoney(summary.takenSales)}`}
          hint={`Completed ₹ ${formatMoney(summary.completedSales)}`}
        />
        <MetricCard
          label="Untaken catalog"
          value={formatNumber(summary.untakenCatalog)}
          hint="Active price-list items never taken in range"
        />
      </div>

      <div className="pca-grid pca-grid--2">
        <section className="pca-card pca-panel--chart">
          <h2 className="pca-card__title">Daily taken services</h2>
          {daily.length ? (
            <ServiceDailyComposedChart data={daily} />
          ) : (
            <p className="pca-empty">No taken services in this range.</p>
          )}
        </section>

        <section className="pca-card pca-panel--chart">
          <h2 className="pca-card__title">Catalog by type</h2>
          {catalogByType.length ? (
            <DistributionDonutChart
              data={catalogByType.map((row) => ({
                name: row.serviceType,
                count: row.count,
              }))}
            />
          ) : (
            <p className="pca-empty">No active price-list services.</p>
          )}
        </section>
      </div>

      <section className="pca-card" style={{ marginTop: 16 }}>
        <h2 className="pca-card__title">Taken services</h2>
        {taken.length ? (
          <div className="pca-table-wrap">
            <table className="pca-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Type</th>
                  <th>Taken</th>
                  <th>Done</th>
                  <th>Pending</th>
                  <th>Analysis</th>
                  <th>Desk</th>
                  <th>Sales</th>
                  <th>In list</th>
                </tr>
              </thead>
              <tbody>
                {taken.map((row) => (
                  <tr key={row.name}>
                    <td>{row.name}</td>
                    <td>{row.serviceType || "—"}</td>
                    <td>{formatNumber(row.count)}</td>
                    <td>{formatNumber(row.completed)}</td>
                    <td>{formatNumber(row.pending)}</td>
                    <td>{formatNumber(row.fromAnalysis)}</td>
                    <td>{formatNumber(row.fromDesk)}</td>
                    <td>₹ {formatMoney(row.sales)}</td>
                    <td>{row.inCatalog ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="pca-empty">No services taken in this range.</p>
        )}
      </section>

      <div className="pca-grid pca-grid--2" style={{ marginTop: 16 }}>
        <section className="pca-card">
          <h2 className="pca-card__title">Untaken catalog (active)</h2>
          {untaken.length ? (
            <ul className="pca-rank-list">
              {untaken.slice(0, 25).map((row) => (
                <li key={row.name} className="pca-rank-list__item">
                  <span>
                    {row.name}
                    <span style={{ opacity: 0.55, marginLeft: 8, fontSize: 12 }}>
                      {row.serviceType}
                    </span>
                  </span>
                  <strong>{row.price != null ? `₹ ${formatMoney(row.price)}` : "—"}</strong>
                </li>
              ))}
            </ul>
          ) : (
            <p className="pca-empty">All active catalog services were taken in this range.</p>
          )}
        </section>
        <section className="pca-card">
          <h2 className="pca-card__title">Staff</h2>
          <p className="pca-empty" style={{ marginBottom: 12 }}>
            Staff performance is tracked separately.
          </p>
          <Link to="/naturals/partner/staff-report" className="pca-btn">
            Open staff analytics →
          </Link>
        </section>
      </div>
    </div>
  );
}
